import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Maximize, Minimize, RefreshCw, Radio, AlertTriangle, ChevronDown, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { WX_EVENT, WX_STORE, localDay, localTime, todayIso } from "@/lib/weather";
import { useFullscreen } from "@/components/telemetry/TelemetryAnalysis";
import { PlacePicker } from "@/components/weather/PlacePicker";
import { RainAlert } from "@/components/weather/RainAlert";
import { CurrentNow } from "@/components/weather/CurrentNow";
import { MinutelyChart } from "@/components/weather/MinutelyChart";
import { HourlyStrip } from "@/components/weather/HourlyStrip";
import { DailyList } from "@/components/weather/DailyList";
import { RadarPanel } from "@/components/weather/RadarPanel";
import { RainMap } from "@/components/weather/RainMap";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const REFRESH_MS = 5 * 60 * 1000;
const STORE = WX_STORE;

function useWakeLock() {
  useEffect(() => {
    let lock = null;
    const req = () => navigator.wakeLock?.request("screen").then((l) => { lock = l; }).catch(() => {});
    const onVis = () => { if (document.visibilityState === "visible") req(); };
    req();
    document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); lock?.release().catch(() => {}); };
  }, []);
}

const Clock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(id); }, []);
  return <span data-testid="weather-clock" className="font-heading font-extrabold text-3xl tabular-nums">{now.toLocaleTimeString("it-IT")}</span>;
};

const AlertItem = ({ a, i, off }) => {
  const { t, lang } = useT();
  const [open, setOpen] = useState(false);
  const when = (dt) => (dt ? `${localDay(dt, off, lang)} ${localTime(dt, off)}` : "—");
  return (
    <div data-testid={`weather-official-alert-${i}`} data-open={open ? "true" : "false"} className="border border-[#FFB800]/30 bg-[#FFB800]/5 rounded-sm px-3 py-1.5 text-[#FFB800]">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span data-testid={`weather-official-alert-${i}-title`} className="font-heading uppercase font-bold text-sm truncate flex-1 min-w-0">{a.event}</span>
        <button type="button" data-testid={`weather-official-alert-${i}-info-btn`} onClick={() => setOpen(!open)} title="Info"
          className="h-7 px-2 flex items-center gap-1 rounded-sm border border-[#FFB800]/40 font-mono text-[10px] uppercase hover:bg-[#FFB800]/15 transition-colors duration-200">
          <Info className="w-3.5 h-3.5" /> Info <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <div data-testid={`weather-official-alert-${i}-details`} className="pl-6 pt-1.5 pb-1 space-y-1">
          <div data-testid={`weather-official-alert-${i}-time`} className="font-mono text-[11px] text-slate-300">{t("wx_alert_from")} {when(a.start)} → {t("wx_alert_to")} {when(a.end)}</div>
          <div className="font-mono text-[10px] uppercase text-[#FFB800]/70">{a.sender_name}</div>
          <div data-testid={`weather-official-alert-${i}-text`} className="font-mono text-xs text-slate-300 whitespace-pre-line">{a.description}</div>
        </div>
      )}
    </div>
  );
};

const Alerts = ({ data }) => (data.alerts || []).map((a, i) => <AlertItem key={i} a={a} i={i} off={data.timezone_offset} />);

function usePlace() {
  const [choice, setChoiceState] = useState(() => { try { return JSON.parse(localStorage.getItem(STORE)) || { mode: "auto" }; } catch { return { mode: "auto" }; } });
  const setChoice = (c) => { localStorage.setItem(STORE, JSON.stringify(c)); setChoiceState(c); window.dispatchEvent(new Event(WX_EVENT)); };
  const [places, setPlaces] = useState(null);
  useEffect(() => { api.get("/weather/places", { params: { today: todayIso() } }).then((r) => setPlaces(r.data)); }, []);
  const live = places?.live?.[0];
  const place = choice.mode === "auto" ? (live ? { name: live.name, lat: live.lat, lon: live.lon, event: live.event_name } : null) : choice;
  return { choice, setChoice, places, place };
}

export default function Weather() {
  const { t, lang } = useT();
  const [fs, setFs] = useFullscreen();
  const { choice, setChoice, places, place } = usePlace();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [windy, setWindy] = useState(false);
  useWakeLock();
  const load = useCallback(async () => {
    if (!place) return;
    setLoading(true);
    try { const r = await api.get("/weather", { params: { lat: place.lat, lon: place.lon, lang } }); setData(r.data); setErr(""); } catch (e) { setErr(formatApiError(e)); } finally { setLoading(false); }
  }, [place?.lat, place?.lon, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setData(null); load(); const id = setInterval(load, REFRESH_MS); return () => clearInterval(id); }, [load]);
  const body = (
    <div data-testid="weather-page" className={fs ? "fixed inset-0 z-[60] bg-[#0A0B10] text-slate-200 overflow-auto p-3 sm:p-5" : ""}>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="min-w-0 mr-auto">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            {choice.mode === "auto" && place && <span className="inline-flex items-center gap-1 text-[#FF3B30] font-bold"><Radio className="w-3 h-3" /> LIVE{place.event ? ` · ${place.event}` : ""}</span>}
            {!fs && <span>{t("wx_title")}</span>}
          </div>
          <h1 data-testid="weather-place-name" className="font-heading font-extrabold uppercase text-4xl sm:text-5xl leading-none truncate">{place?.name || t("wx_title")}</h1>
        </div>
        <Clock />
        <button type="button" data-testid="weather-refresh-btn" onClick={load} title={t("wx_refresh")} className="h-10 w-10 flex items-center justify-center border border-[#282D3D] rounded-sm text-slate-400 hover:text-white transition-colors duration-200">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
        <button type="button" data-testid="weather-fullscreen-btn" onClick={() => setFs(!fs)} className="h-10 px-3 flex items-center gap-2 border border-[#282D3D] rounded-sm font-heading uppercase text-slate-300 hover:text-white transition-colors duration-200">
          {fs ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />} <span className="hidden sm:inline">{t(fs ? "wx_exit_fs" : "wx_fullscreen")}</span>
        </button>
      </div>
      {!fs && <div className="mb-4"><PlacePicker places={places} choice={choice} setChoice={setChoice} /></div>}
      {!place && places && <div data-testid="weather-no-live" className="border border-dashed border-[#282D3D] rounded-sm p-10 text-center font-mono text-sm text-slate-500">{t("wx_no_live")}</div>}
      {err && <div data-testid="weather-error" className="border border-[#FF3B30]/50 bg-[#FF3B30]/10 rounded-sm p-4 font-mono text-sm text-[#FF3B30]">{err}</div>}
      {place && data && (
        <div className="space-y-4">
          <RainAlert data={data} place={place.name} />
          <Alerts data={data} />
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
            <div className="xl:col-span-4"><CurrentNow data={data} /></div>
            <div className="xl:col-span-8"><RainMap lat={place.lat} lon={place.lon} name={place.name} offset={data.timezone_offset} h={fs ? 520 : 440} onWindy={() => setWindy(true)} /></div>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
            <div className="xl:col-span-4"><MinutelyChart data={data} /></div>
            <div className="xl:col-span-8"><HourlyStrip data={data} /></div>
          </div>
          <DailyList data={data} />
          <Dialog open={windy} onOpenChange={setWindy}>
            <DialogContent data-testid="windy-dialog" className="max-w-[96vw] w-[96vw] h-[92vh] bg-[#0A0B10] border-[#282D3D] p-3 flex flex-col z-[70]">
              <DialogTitle className="font-heading uppercase text-lg">{t("wx_windy_map")} · {place.name}</DialogTitle>
              <div className="flex-1 min-h-0"><RadarPanel lat={place.lat} lon={place.lon} h={0} /></div>
            </DialogContent>
          </Dialog>
          <div data-testid="weather-updated" className="font-mono text-[10px] uppercase text-slate-600 text-right">{t("wx_updated")} {localTime(Math.round(data.fetched_at), -new Date().getTimezoneOffset() * 60)} · OpenWeather · Windy</div>
        </div>
      )}
    </div>
  );
  return fs ? createPortal(body, document.body) : body;
}
